import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Category as CategoryIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { NotificationAnalyticsService } from '../../../services/notificationAnalyticsService';
import { NotificationStatsCard } from '../../../components/admin/notifications/NotificationStatsCard';
import { NotificationVolumeChart } from '../../../components/admin/notifications/NotificationVolumeChart';
import { NotificationTypeBreakdown } from '../../../components/admin/notifications/NotificationTypeBreakdown';
import { QuickActionsPanel } from '../../../components/admin/notifications/QuickActionsPanel';
import { RecentNotificationsList } from '../../../components/admin/notifications/RecentNotificationsList';
import {
  NotificationMetrics,
  NotificationTypeMetrics,
  NotificationTrendData,
  DashboardStats,
} from '../../../types/notification-admin';

/**
 * NotificationDashboard Page
 * Main dashboard for notification management and overview
 */
const NotificationDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [overallMetrics, setOverallMetrics] = useState<NotificationMetrics | null>(null);
  const [typeMetrics, setTypeMetrics] = useState<NotificationTypeMetrics[]>([]);
  const [trendData, setTrendData] = useState<NotificationTrendData[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Calculate date range (last 30 days)
      const now = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // Load all data in parallel
      const [stats, metrics, types, trends] = await Promise.all([
        NotificationAnalyticsService.getDashboardStats(),
        NotificationAnalyticsService.getOverallMetrics({ start: thirtyDaysAgo, end: now }),
        NotificationAnalyticsService.getMetricsByType({ start: thirtyDaysAgo, end: now }),
        NotificationAnalyticsService.getTrendData({ start: thirtyDaysAgo, end: now }, 'day'),
      ]);

      setDashboardStats(stats);
      setOverallMetrics(metrics);
      setTypeMetrics(types);
      setTrendData(trends);
    } catch (err: any) {
      console.error('Error loading dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadDashboardData();
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Tooltip title="Back to Admin">
          <IconButton onClick={() => navigate('/admin')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="h4" sx={{ flexGrow: 1, fontWeight: 700 }}>
          Notification Management
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefresh}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <NotificationStatsCard
            title="Sent Today"
            value={dashboardStats?.todaySent || 0}
            icon={<NotificationsIcon />}
            color="primary"
            trend={dashboardStats ? {
              value: dashboardStats.dailyTrend,
              direction: dashboardStats.dailyTrend >= 0 ? 'up' : 'down',
              period: 'vs yesterday',
            } : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <NotificationStatsCard
            title="Read Rate Today"
            value={`${dashboardStats?.todayReadRate.toFixed(1) || 0}%`}
            icon={<CheckCircleIcon />}
            color="success"
            subtitle={`${dashboardStats?.todayRead || 0} of ${dashboardStats?.todaySent || 0} read`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <NotificationStatsCard
            title="Active Types"
            value={dashboardStats?.activeTypes || 0}
            icon={<CategoryIcon />}
            color="info"
            subtitle="In last 30 days"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <NotificationStatsCard
            title="Total Unread"
            value={dashboardStats?.totalUnread || 0}
            icon={<PeopleIcon />}
            color="warning"
            subtitle="Across all users"
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={8}>
          <NotificationVolumeChart data={trendData} />
        </Grid>
        <Grid item xs={12} lg={4}>
          <QuickActionsPanel
            onSendBroadcast={() => navigate('/admin/notifications/broadcast')}
            onViewTemplates={() => navigate('/admin/notifications/templates')}
            onViewAnalytics={() => navigate('/admin/notifications/analytics')}
            onViewTesting={() => navigate('/admin/notifications/testing')}
            onViewHistory={() => navigate('/admin/notifications/history')}
            onViewSettings={() => navigate('/admin/notifications/settings')}
          />
        </Grid>
      </Grid>

      {/* Type Breakdown */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <NotificationTypeBreakdown data={typeMetrics} showTable />
        </Grid>
      </Grid>

      {/* Recent Notifications */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <RecentNotificationsList limit={50} />
        </Grid>
      </Grid>
    </Container>
  );
};

export default NotificationDashboard;
